/**
 * Pager (docs/03 §3, X-6.2) — the one pagination control.
 *
 * v2 grew four of these independently, and the best one was on the screen with
 * the fewest users: `/admin/audit` had "Page 3 of 11", a `disabledReason` on each
 * control so a disabled button says WHY, and the whole block hidden when there
 * is only one page. The three list screens each had a weaker copy. This is the
 * audit screen's behaviour, extracted, plus the row count the list screens
 * showed — so nothing was lost in either direction.
 *
 * It renders nothing at all when everything fits on one page: a pager that
 * cannot page is furniture, and it pushed the last row further from the eye on
 * every short list in the app.
 */

import { clsx } from 'clsx';
import type { ReactNode } from 'react';

import { Button } from './Button.js';

export interface PagerProps {
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly onPage: (page: number) => void;
  /**
   * Plural noun for the count, e.g. "properties". Omit to show only the page
   * position — right for a ledger where the total is an implementation detail.
   */
  readonly noun?: string | undefined;
  /**
   * Direction words. A ledger reads in time ("Newer" / "Older"); a list reads in
   * position. Defaults to position.
   */
  readonly labels?: 'position' | 'time' | undefined;
  readonly className?: string | undefined;
}

export function Pager({
  page,
  pageSize,
  total,
  onPage,
  noun,
  labels = 'position',
  className,
}: PagerProps): ReactNode {
  const pageCount = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  if (total <= pageSize) return null;

  const previous = labels === 'time' ? 'Newer' : 'Previous';
  const next = labels === 'time' ? 'Older' : 'Next';

  return (
    <div className={clsx('flex items-center justify-between gap-3', className)}>
      <span className="text-meta text-ink2">
        {noun === undefined
          ? `Page ${String(page)} of ${String(pageCount)}`
          : `${total.toLocaleString('en-US')} ${noun} · page ${String(page)} of ${String(pageCount)}`}
      </span>
      <span className="flex gap-2">
        <Button
          variant="ghost"
          disabledReason={page <= 1 ? 'This is the first page' : undefined}
          onClick={() => {
            onPage(Math.max(1, page - 1));
          }}
        >
          {previous}
        </Button>
        <Button
          variant="ghost"
          disabledReason={page >= pageCount ? 'This is the last page' : undefined}
          onClick={() => {
            onPage(page + 1);
          }}
        >
          {next}
        </Button>
      </span>
    </div>
  );
}
